import {
  ExpandMore as IconExpandMore
} from '@mui/icons-material';
import {
  Accordion,
  AccordionSummary,
  Box,
  Button,
  Drawer,
  IconButton,
  Popper,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { popperStyles } from 'Components/Shared/ActionOnSelectedItems/popperStyles';
import CustomStatus from 'Components/Shared/Chips/CustomStatus';
import useTranslate from 'Hooks/useTranslate';
import { ReactNode, createRef, useEffect, useState } from 'react';
import TestIds from 'Tests/TestIds';
import { MAX_SELECTED } from 'Theme/config';
import mediaQueries from 'Theme/constants/mediaQueries';

export interface ActionConfig {
  key: string;
  icon: ReactNode;
  tooltip: string;
  testId?: string;
  visible?: boolean;
  disabled?: boolean;
  renderMode: 'accordion' | 'popper';
  renderAccordionContent?: (onCollapse: () => void) => ReactNode;
  renderPopperContent?: (onCollapse: () => void) => ReactNode;
}

interface Props {
  selectedIds: string[];
  selectAllEnabled: boolean;
  selectedCount: number;
  totalCount: number;
  onSelect: (ids: string[] | null) => void;
  disabled?: boolean;
  loading?: boolean;
  searchTerm?: string;
  icon: ReactNode;
  selectionLabel: string;
  allSelectedLabel?: string;
  showMaxSelectedWarning?: boolean;
  showSelectAll?: boolean;
  actions: ActionConfig[];
}

const styles = (theme: Theme) =>
  createStyles({
    drawer: {
      borderRadius: '20px 20px 0 0',
      maxHeight: '90%'
    },
    expansionPanel: {
      backgroundColor: 'transparent'
    },
    expansionPanelSummary: {
      cursor: 'auto',
      // Required for the text ellipsis
      overflow: 'hidden'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center'
    },
    headerLeftIcon: {
      marginRight: theme.spacing(1),
      verticalAlign: 'middle'
    },
    ...popperStyles(theme)
  });

function ActionOnSelectedItems(props: Props & WithStyles<typeof styles>) {
  const ts = useTranslate();
  const isMobileP = useMediaQuery(mediaQueries.mobilePortrait);
  const {
    selectedCount,
    totalCount,
    selectedIds,
    selectAllEnabled,
    onSelect,
    disabled = false,
    classes,
    searchTerm = '',
    loading = false,
    icon,
    selectionLabel,
    allSelectedLabel,
    showMaxSelectedWarning = true,
    showSelectAll = true,
    actions
  } = props;

  const [displayAction, setDisplayAction] = useState<string | null>(null);
  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const buttonRef = createRef<HTMLButtonElement>();

  useEffect(() => {
    if (selectedCount <= 0 && Boolean(displayAction)) {
      // If the drawer is expanded and all items are unselected, collapse it
      setDisplayAction(null);
    }
  }, [selectedCount, displayAction]);

  const visibleActions = actions.filter(a => a.visible !== false);
  const activeAction = visibleActions.find(a => a.key === displayAction);

  const isExpanded =
    Boolean(activeAction) &&
    (activeAction!.renderMode === 'accordion');

  const label =
    allSelectedLabel &&
      selectedCount === totalCount &&
      searchTerm.length === 0
      ? allSelectedLabel
      : selectionLabel;

  function onCollapse() {
    setDisplayAction(null);
  }

  function onUnselect() {
    setDisplayAction(null);
    onSelect([]);
  }

  return (
    <>
      {selectedCount > 0 && <div style={{ height: '72px' }} />}
      <Drawer
        anchor="bottom"
        variant={isExpanded ? 'temporary' : 'persistent'}
        open={selectedCount > 0}
        classes={{ paper: classes.drawer }}
      >
        <Accordion
          expanded={isExpanded}
          classes={{ root: classes.expansionPanel }}
        >
          <AccordionSummary
            classes={{ content: classes.expansionPanelSummary }}
            expandIcon={
              isExpanded ? (
                <IconExpandMore
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (displayAction) {
                      setDisplayAction(null);
                    }
                  }}
                />
              ) : null
            }
          >
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              // Required for the text ellipsis
              overflow: 'hidden',
              flexDirection: isMobileP ? 'column' : 'row'
            }}>
              <div>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {icon}
                  <div>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      component="div"
                      classes={{ root: classes.headerLeft }}
                    >
                      {label}
                    </Typography>
                    {showMaxSelectedWarning &&
                      !selectAllEnabled &&
                      selectedIds.length > MAX_SELECTED &&
                      isMobileP && (
                        <CustomStatus
                          type="error"
                          label={ts('table.maxSelection', {
                            value: MAX_SELECTED
                          })}
                        />
                      )}
                  </div>
                </Box>
              </div>
              <div>
                {visibleActions.map(action => (
                  <Tooltip key={action.key} title={action.tooltip}>
                    <IconButton
                      ref={action.renderMode === 'popper' ? buttonRef : undefined}
                      component="button"
                      data-testid={action.testId}
                      color={
                        displayAction === action.key
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() => {
                        setDisplayAction(action.key);
                        if (action.renderMode === 'popper') {
                          setAnchorEl(buttonRef.current);
                        }
                      }}
                      disabled={action.disabled ?? disabled}
                      size="large"
                    >
                      {action.icon}
                    </IconButton>
                  </Tooltip>
                ))}
              </div>
              <div>
                {showMaxSelectedWarning &&
                  !selectAllEnabled &&
                  selectedIds.length > MAX_SELECTED &&
                  !isMobileP &&
                  !loading && (
                    <CustomStatus
                      type="error"
                      label={ts('table.maxSelection', {
                        value: MAX_SELECTED
                      })}
                    />
                  )}
                {showSelectAll &&
                  totalCount <= MAX_SELECTED &&
                  !selectAllEnabled &&
                  !loading && (
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => onSelect(null)}
                      data-testid={
                        TestIds.table.selectAllElement.selectAllButton
                      }
                      style={{ marginLeft: '8px' }}
                    >
                      {ts('table.selectAll')}
                    </Button>
                  )}

                {selectAllEnabled && !loading && (
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={() => onUnselect()}
                    data-testid={
                      TestIds.table.selectAllElement.unselectAllButton
                    }
                    style={{ marginLeft: '8px' }}
                  >
                    {ts('table.unselectAll')}
                  </Button>
                )}
              </div>
            </div>
          </AccordionSummary>
          {visibleActions
            .filter(a => displayAction === a.key && a.renderMode === 'accordion' && a.renderAccordionContent)
            .map(a => (
              <div key={a.key}>
                {a.renderAccordionContent!(onCollapse)}
              </div>
            ))}
        </Accordion>
      </Drawer>
      {visibleActions
        .filter(a => displayAction === a.key && a.renderMode === 'popper' && a.renderPopperContent)
        .map(a => (
          <Popper
            key={a.key}
            open={true}
            placement="top"
            anchorEl={anchorEl}
            modifiers={[
              {
                name: 'arrow',
                enabled: true,
                options: {
                  element: arrowRef
                }
              }
            ]}
            className={classes.popper}
          >
            <span
              className={classes.arrow}
              ref={element => setArrowRef(element)}
            />
            {a.renderPopperContent!(onCollapse)}
          </Popper>
        ))}
    </>
  );
}

export default withStyles(styles)(ActionOnSelectedItems);
